import { useId, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Percent, ShoppingBag, X } from "lucide-react";
import { Button } from "@/shared/ui/core";
import { useFocusTrap } from "@/shared/lib/useFocusTrap";

export interface PackageUpsellDialogProps {
  /** Solo se monta abierto; cerrar es desmontarlo. */
  onClose: () => void;
  /** "Comprar solo este pedido": sigue al carrito, sin paquete. */
  onOnlyThisOrder: () => void;
}

/**
 * ═══════════════════════════════════════════════════════════════════
 * El CTA hacia paquetes, justo después de "Añadir al carrito".
 *
 * Es el punto III del journey de Anahí (26-ago): en la etapa temprana
 * el llamado NO va hacia la franquicia — va hacia adquirir un paquete,
 * con el ahorro del 15 % de la categoría personal por delante. Quien
 * solo quiere su pedido lo dice con un botón y sigue su camino al
 * carrito; nadie queda atrapado en la venta.
 *
 * El 15 % no es un número de marketing inventado aquí: es el techo de
 * descuento de los paquetes de RECOMPRA PERSONAL (los de negocio
 * digital llegan al 30 %, pero ese mensaje es de otra etapa del
 * journey).
 * ═══════════════════════════════════════════════════════════════════
 */
export function PackageUpsellDialog({ onClose, onOnlyThisOrder }: PackageUpsellDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useFocusTrap(panelRef, true, { onEscape: onClose });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* El fondo cierra: tocar fuera es la forma natural de "ahora no". */}
      <button
        type="button"
        aria-label={t("Cerrar")}
        className="absolute inset-0 bg-ds-ink/30 backdrop-blur-sm"
        onClick={onClose}
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative z-10 w-full max-w-md animate-apple-fade-in rounded-3xl border border-ds-border bg-white p-6 shadow-ds-float"
      >
        <div className="flex items-start justify-between gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-ds-lavender-bg-2 text-ds-violet-pop">
            <Percent className="h-5 w-5" />
          </span>
          <button
            type="button"
            aria-label={t("Cerrar")}
            onClick={onClose}
            className="rounded-full p-1 text-ds-ink-3 hover:text-ds-ink"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <h2 id={titleId} className="mt-4 text-[19px] font-semibold text-ds-ink">
          {t("¿Quieres hasta un 15% de descuento?")}
        </h2>
        <p className="mt-2 text-[14px] leading-relaxed text-ds-ink-3">
          {t(
            "Adquiere un paquete y ahorra en esta compra y en las que siguen. Tu carrito se queda tal cual.",
          )}
        </p>

        <div className="mt-5 grid gap-2">
          <Button size="lg" fullWidth onClick={() => navigate("/paquetes")}>
            {t("Elegir un paquete")}
          </Button>
          <Button
            variant="secondary"
            size="lg"
            fullWidth
            leftIcon={<ShoppingBag />}
            onClick={onOnlyThisOrder}
          >
            {t("Comprar solo este pedido")}
          </Button>
        </div>
      </div>
    </div>
  );
}
